import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Card,
  Spin,
  Tabs,
  Modal,
  Form,
  Input,
  Upload,
  Select,
  message,
} from "antd";
import { PlusOutlined, UploadOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { TabsProps } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Category {
  id: number;
  name: string;
}

interface Product {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  category_id: number;
  sub_category_id: number;
  title: string;
  title_kz?: string;
  description: string;
  description_kz?: string;
  code: string;
  images: string[];
}

interface SubCategory {
  id: number;
  name: string;
}

const CATEGORIES: Category[] = [
  { id: 1, name: "Протезно-ортопедичесике средства" },
  { id: 2, name: "Обувь" },
  { id: 5, name: "Лечебно-профилактические изделия" },
  { id: 6, name: "Технические средства реабилитации" },
];

const { Option } = Select;

const ProductCreatePage: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState<number>(CATEGORIES[0].id);
  const [modalVisible, setModalVisible] = useState(false);
  const [subcategories, setSubcategories] = useState<SubCategory[]>([]);
  const [subLoading, setSubLoading] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [descriptionHtml, setDescriptionHtml] = useState<string>("");
  const [descriptionKzHtml, setDescriptionKzHtml] = useState<string>("");
  const [fileList, setFileList] = useState<any[]>([]);
  const [initialImageUrls, setInitialImageUrls] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/product/`);
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error("Ошибка загрузки товаров");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubcategories = async (category_id: number) => {
    setSubLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/categories/${category_id}/subcategories`);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSubcategories(Array.isArray(data) ? data : []);
    } catch (e) {
      setSubcategories([]);
    } finally {
      setSubLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchSubcategories(activeCategoryId);
  }, [activeCategoryId, modalVisible]);

  useEffect(() => {
    if (editingProduct) {
      fetchSubcategories(editingProduct.category_id);
    }
  }, [editingProduct]);

  useEffect(() => {
    if (editingProduct) {
      setDescriptionHtml(editingProduct.description || "");
      setDescriptionKzHtml(editingProduct.description_kz || "");
      form.setFieldsValue({
        sub_category_id: editingProduct.sub_category_id,
        title: editingProduct.title,
        title_kz: editingProduct.title_kz || "",
        code: editingProduct.code,
        images: (editingProduct.images || []).map((url, idx) => ({
          uid: String(idx),
          name: url.split("/").pop() || `image${idx}`,
          status: "done",
          url,
        })),
        description: editingProduct.description || "",
        description_kz: editingProduct.description_kz || "",
      });
      setActiveCategoryId(editingProduct.category_id);
    } else {
      setDescriptionHtml("");
      setDescriptionKzHtml("");
      form.resetFields();
    }
  }, [editingProduct, modalVisible, form]);

  const filteredProducts = products.filter((p) => p.category_id === activeCategoryId);

  const openCreateModal = () => {
    setEditingProduct(null);
    form.resetFields();
    setDescriptionHtml("");
    setDescriptionKzHtml("");
    setFileList([]);                 // ← пустой список
    setInitialImageUrls([]);         // ← нет исходных
    setModalVisible(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    // исходные URL — пригодятся, чтобы понять, что удалили
    const mapped = (product.images || []).map((url, idx) => ({
      uid: String(idx),
      name: url.split("/").pop() || `image${idx}`,
      status: "done",
      url,
    }));
    setFileList(mapped);
    setInitialImageUrls(product.images || []);
    setModalVisible(true);
  };
  

  const showDeleteConfirm = (product: Product) => {
    Modal.confirm({
      title: `Удалить товар "${product.title}"?`,
      content: "Вы уверены, что хотите удалить этот товар?",
      okText: "Удалить",
      cancelText: "Отмена",
      okType: "danger",
      onOk: async () => {
        try {
          const res = await fetch(`${API_BASE_URL}/product/${product.ID}`, {
            method: "DELETE",
          });
          if (!res.ok) throw new Error();
          message.success("Товар удален");
          fetchProducts();
        } catch {
          message.error("Ошибка при удалении товара");
        }
      },
    });
  };

  const handleCreateOrEditProduct = async (values: any) => {
    if (saving) return;           
    setSaving(true);
  
    const isEdit = Boolean(editingProduct);
    const formData = new FormData();
    const categoryIdToUse = isEdit ? editingProduct!.category_id : activeCategoryId;
  
    formData.append("category_id", String(categoryIdToUse));
    formData.append("sub_category_id", values.sub_category_id);
    formData.append("title", values.title);
    formData.append("title_kz", values.title_kz || "");
    formData.append("description", descriptionHtml);
    formData.append("description_kz", descriptionKzHtml);
    formData.append("code", values.code || "");
  
    if (isEdit) {
      const keptUrls = fileList.filter(f => !!f.url).map(f => f.url as string);
      const removed = initialImageUrls.filter(u => !keptUrls.includes(u));
      removed.forEach(u => formData.append("delete[]", u));
    }
  
    fileList.forEach((f: any) => {
      if (f.originFileObj) {
        formData.append("images", f.originFileObj);
      }
    });
  
    try {
      const url = isEdit
        ? `${API_BASE_URL}/product/${editingProduct!.ID}`
        : `${API_BASE_URL}/product/`;
      const method = isEdit ? "PUT" : "POST";
  
      const res = await fetch(url, { method, body: formData });
  
      if (res.status === 413) {
        message.error("Слишком большой размер загружаемых файлов");
        return;
      }
      if (res.status === 409) {
        message.error("Такой код уже существует");
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        message.error(err?.error || "Сохранение товара невозможно — размер загружаемой фотографии слишком велик.");
        return;
      }
  
      message.success(isEdit ? "Товар обновлен!" : "Товар успешно создан!");
      setModalVisible(false);
      form.resetFields();
      setFileList([]);
      setInitialImageUrls([]);
      fetchProducts();
    } catch {
      message.error("Ошибка сохранения товара");
    } finally {
      setSaving(false);           // обязательно снять флаг
    }
  };
  
  
  const columns = [
    { title: "ID", dataIndex: "ID", key: "ID", width: 60 },
    { title: "Название", dataIndex: "title", key: "title" },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
      render: (value: string) => (
        <div
          style={{
            maxWidth: 300,
            overflow: "hidden",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
          }}
          dangerouslySetInnerHTML={{ __html: value }}
        />
      ),
    },
    { title: "Код", dataIndex: "code", key: "code", width: 100 },
    {
      title: "Фото",
      dataIndex: "images",
      key: "images",
      render: (images: string[]) =>
        images && images.length ? (
          <img src={images[0]} alt="preview" style={{ width: 70, height: 50, objectFit: "cover" }} />
        ) : (
          <span>—</span>
        ),
      width: 80,
    },
    {
      title: "Подкатегория",
      dataIndex: "sub_category_id",
      key: "sub_category_id",
      render: (id: number) => {
        const subcat = subcategories.find((s) => s.id === id);
        return subcat ? subcat.name : id;
      },
      width: 120,
    },
    {
      title: "Действия",
      key: "actions",
      width: 130,
      render: (_: any, record: Product) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEditModal(record)}
          >
            Редактировать
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
            onClick={() => showDeleteConfirm(record)}
          />
        </div>
      ),
    },
  ];

  const tabItems: TabsProps["items"] = CATEGORIES.map((cat) => ({
    label: cat.name,
    key: String(cat.id),
  }));

  // ===== Форма =====

  return (
    <Card
      title="Товары"
      extra={
        <Button
          icon={<PlusOutlined />}
          type="primary"
          onClick={openCreateModal}
        >
          Добавить
        </Button>
      }
    >
      <Tabs
        items={tabItems}
        activeKey={String(activeCategoryId)}
        onChange={(key) => setActiveCategoryId(Number(key))}
        style={{ marginBottom: 20 }}
      />
      <Spin spinning={loading}>
        <Table
          dataSource={filteredProducts}
          columns={columns}
          rowKey="ID"
          pagination={{ pageSize: 10 }}
        />
      </Spin>
      <Modal
       title={editingProduct ? "Редактировать товар" : "Добавить товар"}
       open={modalVisible}
       onCancel={() => !saving && setModalVisible(false)}
       footer={null}
       destroyOnClose
       width={800}
       maskClosable={!saving}   
       closable={!saving}       
       keyboard={!saving}       
     >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateOrEditProduct}
          initialValues={{ category_id: activeCategoryId }}
        >
          <Form.Item
            label="Подкатегория"
            name="sub_category_id"
            rules={[{ required: true, message: "Выберите подкатегорию" }]}
          >
            <Select
              placeholder="Выберите подкатегорию"
              loading={subLoading}
              allowClear
              showSearch
              optionFilterProp="children"
              disabled={subcategories.length === 0}
            >
              {subcategories.map((sub) => (
                <Option key={sub.id} value={sub.id}>
                  {sub.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Название (рус)"
            name="title"
            rules={[{ required: true, message: "Введите название товара" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Аты (қаз)"
            name="title_kz"
            rules={[{ required: true, message: "Қазақша атауын енгізіңіз" }]}
          >
            <Input />
          </Form.Item>

          {/* Описание (рус) */}
          <Form.Item
            label="Описание (рус)"
            name="description"
            rules={[
              {
                validator: (_, value) =>
                  !descriptionHtml || descriptionHtml === "<p><br></p>"
                    ? Promise.reject("Введите описание")
                    : Promise.resolve(),
              },
            ]}
            validateFirst
          >
            <>
              <ReactQuill
                value={descriptionHtml}
                onChange={setDescriptionHtml}
                theme="snow"
                placeholder="Опишите товар подробно (характеристики, материалы, комплектация и т.д.)"
                style={{
                  height: 300,
                  minHeight: 340,
                  maxHeight: 520,
                  marginBottom: 30,
                  background: "#fff",
                  borderRadius: 6,
                  fontSize: 16,
                  padding: 0,
                }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ align: [] }],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
                formats={[
                  "header",
                  "bold",
                  "italic",
                  "underline",
                  "strike",
                  "list",
                  "bullet",
                  "align",
                  "link",
                  "image",
                ]}
              />
              {/* ДОБАВЛЯЕМ ОТСТУП ДЛЯ ошибки */}
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.description !== curr.description}>
                {() => (
                  <div style={{ height: 12 }} />
                )}
              </Form.Item>
            </>
          </Form.Item>

          {/* Сипаттамасы (қаз) */}
          <Form.Item
            label="Сипаттамасы (қаз)"
            name="description_kz"
            rules={[
              {
                validator: (_, value) =>
                  !descriptionKzHtml || descriptionKzHtml === "<p><br></p>"
                    ? Promise.reject("Қазақша сипаттаманы енгізіңіз")
                    : Promise.resolve(),
              },
            ]}
            validateFirst
          >
            <>
              <ReactQuill
                value={descriptionKzHtml}
                onChange={setDescriptionKzHtml}
                theme="snow"
                placeholder="Өнім туралы толық сипаттама"
                style={{
                  height: 300,
                  minHeight: 340,
                  maxHeight: 520,
                  marginBottom: 30,
                  background: "#fff",
                  borderRadius: 6,
                  fontSize: 16,
                  padding: 0,
                }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ align: [] }],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
                formats={[
                  "header",
                  "bold",
                  "italic",
                  "underline",
                  "strike",
                  "list",
                  "bullet",
                  "align",
                  "link",
                  "image",
                ]}
              />
              {/* ДОБАВЛЯЕМ ОТСТУП ДЛЯ ошибки */}
              <Form.Item noStyle shouldUpdate={(prev, curr) => prev.description_kz !== curr.description_kz}>
                {() => (
                  <div style={{ height: 12 }} />
                )}
              </Form.Item>
            </>
          </Form.Item>

          <Form.Item label="Код" name="code">
            <Input placeholder="Артикул, код товара (необязательно)" />
          </Form.Item>
          <Form.Item label="Изображения" name="images">
  <Upload
    multiple
    listType="picture"
    beforeUpload={() => false}
    accept="image/*"
    maxCount={10}
    fileList={fileList}                        // ← контролируем
    onChange={({ fileList }) => setFileList(fileList)} // ← обновляем
  >
    <Button icon={<UploadOutlined />}>Загрузить</Button>
  </Upload>
</Form.Item>

          <Form.Item>
          <Button type="primary" htmlType="submit" block loading={saving} disabled={saving}>
          {editingProduct ? "Сохранить" : "Создать товар"}
        </Button>

          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProductCreatePage;
